import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import type { GetOfficeApiSuccessResponse } from "@/api/office/[id]/route";
import { Address } from "@/components/atoms/Address";
import InputContainer from "@/components/forms/controls/InputContainer";
import { OfficeSelectorModal } from "@/components/organisms/OfficeSelectorModal";
import { env } from "@/env";
import { Office } from "@/graphql/sdk/__generated__";
import { cn } from "@/helpers/className";
import useFormError from "@/hooks/useFormError";
import { useTranslation } from "@/i18n/client";
import { AllowedLanguages } from "@/types/generic";

interface OfficeSelectorProps
  extends Omit<React.ComponentProps<"input">, "id" | "aria-describedby"> {
  hiddenLabel?: boolean;
  id?: string;
  inputClassname?: string;
  label: string;
  labelClasses?: string;
  locale: AllowedLanguages;
  name: string;
}

export const OfficeSelector = ({
  label,
  hiddenLabel = false,
  name,
  labelClasses,
  inputClassname,
  className,
  locale,
  ...props
}: OfficeSelectorProps) => {
  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();
  const { t } = useTranslation(locale);

  const error = useFormError(name, errors);
  const id = props.id ?? name;

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState<Office | null>(null);

  const showModal = () => {
    setModalOpen(true);
  };

  const onSelectOffice = (office: Office) => {
    setValue(name, office.id);
    setSelectedOffice(office);
    setModalOpen(false);
  };

  const watchOfficeId = watch(name);

  useEffect(() => {
    // OfficeId was filled from other source (e.g.: prefilled form because user is logged in)
    if (watchOfficeId && !selectedOffice) {
      fetch(
        `${env.NEXT_PUBLIC_BASE_URL}/api/office/${watchOfficeId}?langcode=${locale}`,
      )
        .then((response) => response.json())
        .then((data: GetOfficeApiSuccessResponse) => {
          if (data.office) {
            setSelectedOffice(data.office);
          } else {
            setValue(name, null);
            setSelectedOffice(null);
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, [watchOfficeId]);

  return (
    <>
      <InputContainer className={className} isError={Boolean(error)}>
        <label
          className={cn(
            labelClasses,
            hiddenLabel && "sr-only",
            props.required && "label-required",
          )}
          htmlFor={id}
        >
          {label}
        </label>
        <div className="w-full">
          <input
            {...register(name)}
            data-error={error ? "true" : undefined}
            className={cn("form-input", inputClassname)}
            id={id}
            type="hidden"
            disabled
            {...props}
          />

          {selectedOffice ? (
            <div className="rounded border bg-white px-4 py-2">
              <strong>{t("components.office_selector.selected_office")}</strong>
              <div className="mb-1 text-sm">{selectedOffice.title}</div>
              {selectedOffice.address && (
                <Address
                  address={selectedOffice.address}
                  className="mb-0 grid-cols-[16px_1fr] gap-x-1 gap-y-1 text-xs"
                  locale={locale}
                />
              )}
              <button
                onClick={showModal}
                type="button"
                className="link text-sm"
              >
                {t("components.office_selector.btn.change_office")}
              </button>
            </div>
          ) : (
            <button onClick={showModal} type="button" className="link">
              {t("components.office_selector.btn.select_office")}
            </button>
          )}
          {error && (
            <div className="form-error mt-2">
              {t("components.office_selector.error.office_required")}
            </div>
          )}
        </div>
      </InputContainer>

      <OfficeSelectorModal
        open={modalOpen}
        setOpen={setModalOpen}
        onSelectOffice={onSelectOffice}
        locale={locale}
      />
    </>
  );
};

export default OfficeSelector;
